// App entry: lazily boots the full app, the MCP consent screen or the logged-out email-link pages; Sentry and both
// stylesheets load statically (Tailwind utilities last, so `.md:hidden` beats `.btn`). A failed boot chunk reloads
// once, then renders `LoadingError` (unreported when site storage is blocked); each target registers a PostHog client.

import * as Sentry from '@sentry/browser'
import { Route, Router } from '@solidjs/router'
import { render } from 'solid-js/web'
import '@fontsource-variable/inter'
import '@fontsource/poppins/400.css'
import '@fontsource/poppins/500.css'
import '@fontsource/poppins/600.css'
import '@fontsource/poppins/700.css'
import './App.scss'
import './index.css'
import { bootTarget, MCP_CONSENT_LOGIN_PENDING } from './shared/boot-target'
import { claimStaleChunkReload } from './shared/helpers/stale-chunk-reload'
import { isStorageBlockedError } from './shared/helpers/storage-blocked'
import { initSentry } from './shared/services/sentry'

initSentry()

let staleChunkReloadStarted = false

const reloadForStaleChunk = (): boolean => {
  if (staleChunkReloadStarted) return true
  if (!claimStaleChunkReload(() => window.sessionStorage, Date.now())) return false
  staleChunkReloadStarted = true
  window.location.reload()
  return true
}

window.addEventListener('vite:preloadError', () => {
  reloadForStaleChunk()
})

const root = document.getElementById('root')

if (import.meta.env.DEV && !(root instanceof HTMLElement)) {
  throw new Error(
    'Root element not found. Did you forget to add it to your index.html? Or maybe the id attribute got misspelled?'
  )
}

const renderBootFailure = async (error: unknown): Promise<void> => {
  if (!isStorageBlockedError(error)) Sentry.captureException(error)
  const [{ TransProvider }, { default: LoadingError }, { initTranslations }] = await Promise.all([
    import('@mbarzda/solid-i18next'),
    import('./components/LoadingError'),
    import('./shared/i18n')
  ])
  await initTranslations()
  render(
    () => (
      <TransProvider>
        <LoadingError hideContact onRetry={() => window.location.reload()} />
      </TransProvider>
    ),
    root!
  )
}

const loadBootModules = async <T,>(modules: Promise<T>): Promise<T> => {
  try {
    const loaded = await modules
    if (!staleChunkReloadStarted) return loaded
  } catch (error) {
    if (!reloadForStaleChunk()) await renderBootFailure(error)
  }
  return await new Promise<T>(() => undefined)
}

const consentLoginPending = (): boolean => {
  try {
    return sessionStorage.getItem(MCP_CONSENT_LOGIN_PENDING) === 'true'
  } catch {
    return false
  }
}

const renderConsentApp = async (): Promise<void> => {
  const [{ default: McpConsentApp }, { AuthProvider }, { getPostHogService }, { queueLandingTouch }] =
    await loadBootModules(
      Promise.all([
        import('./components/mcp/McpConsentApp'),
        import('./shared/providers/auth-provider'),
        import('./shared/services/tracking-service'),
        import('./shared/helpers/touch-queue')
      ])
    )

  getPostHogService()
  queueLandingTouch()

  render(
    () => (
      <Router root={(props) => <AuthProvider>{props.children}</AuthProvider>}>
        <Route path="*" component={McpConsentApp} />
      </Router>
    ),
    root!
  )
}

const renderEmailApp = async (): Promise<void> => {
  const [
    { default: EmailLinkApp },
    { getPostHogService },
    { emailLanding },
    { trackEmailLinkLanded },
    { queueLandingTouch }
  ] = await loadBootModules(
    Promise.all([
      import('./components/email-links/EmailLinkApp'),
      import('./shared/services/tracking-service'),
      import('./shared/helpers/email-landing'),
      import('./shared/helpers/tracking-events'),
      import('./shared/helpers/touch-queue')
    ])
  )

  getPostHogService()
  const landing = emailLanding(window.location.search)
  if (landing) trackEmailLinkLanded(landing)
  queueLandingTouch()

  render(
    () => (
      <Router>
        <Route path="*" component={EmailLinkApp} />
      </Router>
    ),
    root!
  )
}

const addIntercomSnippet = (): void => {
  const appId = import.meta.env.VITE_INTERCOM_APP_ID
  ;(function () {
    const w = window
    const ic = w.Intercom
    if (typeof ic === 'function') {
      ic('reattach_activator')
      return
    }

    const d = document
    const queue = function (...args: unknown[]): void {
      queue.c(args)
    } as Window['Intercom'] & { q: unknown[][]; c: (args: unknown[]) => void }
    queue.q = []
    queue.c = (args): void => {
      queue.q.push(args)
    }
    w.Intercom = queue

    const load = (): void => {
      const script = d.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.src = `https://widget.intercom.io/widget/${appId}`
      const firstScript = d.getElementsByTagName('script')[0]
      firstScript.parentNode?.insertBefore(script, firstScript)
    }

    if (document.readyState === 'complete') load()
    else w.addEventListener('load', load, false)
  })()
}

const renderStandardApp = async (): Promise<void> => {
  const [
    { default: App, AppRouteTable },
    { CorisProvider },
    { TrackingProvider },
    { getPostHogService },
    { patchUrlSearchParamsCommas },
    { emailLanding },
    { trackEmailLinkLanded },
    { queueLandingTouch }
  ] = await loadBootModules(
    Promise.all([
      import('./App'),
      import('./shared/providers/coris/coris.provider'),
      import('./shared/providers/tracking.provider'),
      import('./shared/services/tracking-service'),
      import('./shared/helpers/patch-url-search-params'),
      import('./shared/helpers/email-landing'),
      import('./shared/helpers/tracking-events'),
      import('./shared/helpers/touch-queue')
    ])
  )

  patchUrlSearchParamsCommas()
  getPostHogService()
  const landing = emailLanding(window.location.search)
  if (landing) trackEmailLinkLanded(landing)
  queueLandingTouch()

  addIntercomSnippet()
  render(
    () => (
      <Router
        root={(props) => (
          <TrackingProvider>
            <CorisProvider>
              <App {...props} />
            </CorisProvider>
          </TrackingProvider>
        )}
      >
        <AppRouteTable />
      </Router>
    ),
    root!
  )
}

const target = bootTarget(window.location.pathname, window.location.search, consentLoginPending())
void (target === 'email' ? renderEmailApp() : target === 'consent' ? renderConsentApp() : renderStandardApp())
